/**
 * Jobs Store
 * 
 * Zustand store for managing background jobs (Optimization, WFA).
 * Provides unified state management for job lifecycle, console attachment, and UI state.
 */

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import type {
    Job,
    JobStatus,
    JobLogLine,
    ConsoleAttachment,
} from '../types/jobs';
import { isJobTerminal } from '../types/jobs';

/**
 * Jobs store state
 */
interface JobsState {
    // Job collections
    jobs: Map<string, Job>;
    
    // Console state
    console: ConsoleAttachment;
    
    // UI state
    isConsolePanelOpen: boolean;
    isJobsSidebarOpen: boolean;
    
    // Connection state
    isConnected: boolean;
    lastError: string | null;
    
    // Loading states
    isLoading: boolean;
    isPolling: boolean;
}

/**
 * Jobs store actions
 */
interface JobsActions {
    // Job management
    addJob: (job: Job) => void;
    updateJob: (jobId: string, updates: Partial<Job>) => void;
    updateJobStatus: (jobId: string, status: JobStatus) => void;
    updateJobProgress: (jobId: string, progress: Job['progress']) => void;
    removeJob: (jobId: string) => void;
    setJobs: (jobs: Job[]) => void;
    
    // Console management
    attachConsole: (jobId: string) => void;
    detachConsole: () => void;
    appendLogs: (logs: JobLogLine[]) => void;
    clearConsoleLogs: () => void;
    setAutoScroll: (enabled: boolean) => void;
    setLastSeq: (seq: number) => void;
    
    // UI state
    openConsolePanel: () => void;
    closeConsolePanel: () => void;
    toggleConsolePanel: () => void;
    openJobsSidebar: () => void;
    closeJobsSidebar: () => void;
    toggleJobsSidebar: () => void;
    
    // Connection state
    setConnected: (connected: boolean) => void;
    setError: (error: string | null) => void;
    setLoading: (loading: boolean) => void;
    setPolling: (polling: boolean) => void;
    
    // Derived state getters (computed from jobs Map)
    getActiveJobs: () => Job[];
    getQueuedJobs: () => Job[];
    getCompletedJobs: () => Job[];
    getFailedJobs: () => Job[];
    getJobById: (jobId: string) => Job | undefined;
    getAttachedJob: () => Job | undefined;
    getJobCounts: () => { active: number; queued: number; completed: number; failed: number };
}

type JobsStore = JobsState & JobsActions;

/**
 * Initial state
 */
const initialState: JobsState = {
    jobs: new Map(),
    console: {
        jobId: null,
        autoScroll: true,
        logs: [],
        lastSeq: 0,
    },
    isConsolePanelOpen: false,
    isJobsSidebarOpen: false,
    isConnected: false,
    lastError: null,
    isLoading: false,
    isPolling: false,
};

/**
 * Create the Jobs store
 */
export const useJobsStore = create<JobsStore>()(
    devtools(
        persist(
            (set, get) => ({
                ...initialState,

                // Job management
                addJob: (job: Job) => {
                    set((state) => {
                        const newJobs = new Map(state.jobs);
                        newJobs.set(job.id, job);
                        return { jobs: newJobs };
                    }, false, 'addJob');
                },

                updateJob: (jobId: string, updates: Partial<Job>) => {
                    set((state) => {
                        const job = state.jobs.get(jobId);
                        if (!job) {
                            return state;
                        }
                        
                        const newJobs = new Map(state.jobs);
                        newJobs.set(jobId, { ...job, ...updates });
                        return { jobs: newJobs };
                    }, false, 'updateJob');
                },

                updateJobStatus: (jobId: string, status: JobStatus) => {
                    set((state) => {
                        const job = state.jobs.get(jobId);
                        if (!job) return state;
                        
                        const newJobs = new Map(state.jobs);
                        const updatedJob = { 
                            ...job, 
                            status,
                            timing: {
                                ...job.timing,
                                ...(isJobTerminal(status) ? { endedAt: new Date().toISOString() } : {}),
                            },
                        };
                        newJobs.set(jobId, updatedJob);
                        return { jobs: newJobs };
                    }, false, 'updateJobStatus');
                },

                updateJobProgress: (jobId: string, progress: Job['progress']) => {
                    set((state) => {
                        const job = state.jobs.get(jobId);
                        if (!job) return state;
                        
                        const newJobs = new Map(state.jobs);
                        newJobs.set(jobId, { 
                            ...job, 
                            progress,
                            timing: {
                                ...job.timing,
                                heartbeatAt: new Date().toISOString(),
                            },
                        });
                        return { jobs: newJobs };
                    }, false, 'updateJobProgress');
                },

                removeJob: (jobId: string) => {
                    set((state) => {
                        const newJobs = new Map(state.jobs);
                        newJobs.delete(jobId);
                        
                        // Clear console if attached to this job
                        const newConsole = state.console.jobId === jobId
                            ? { ...state.console, jobId: null, logs: [], lastSeq: 0 }
                            : state.console;
                        
                        return { jobs: newJobs, console: newConsole };
                    }, false, 'removeJob');
                },

                setJobs: (jobs: Job[]) => {
                    set(() => {
                        const newJobs = new Map<string, Job>();
                        jobs.forEach(job => newJobs.set(job.id, job));
                        return { jobs: newJobs };
                    }, false, 'setJobs');
                },

                // Console management
                attachConsole: (jobId: string) => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            jobId,
                            logs: [], // Clear logs when attaching new job
                            lastSeq: 0,
                        },
                        isConsolePanelOpen: true, // Auto-open console when attaching
                    }), false, 'attachConsole');
                },

                detachConsole: () => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            jobId: null,
                            logs: [],
                            lastSeq: 0,
                        },
                    }), false, 'detachConsole');
                },

                appendLogs: (logs: JobLogLine[]) => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            logs: [...state.console.logs, ...logs],
                        },
                    }), false, 'appendLogs');
                },

                clearConsoleLogs: () => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            logs: [],
                        },
                    }), false, 'clearConsoleLogs');
                },

                setAutoScroll: (enabled: boolean) => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            autoScroll: enabled,
                        },
                    }), false, 'setAutoScroll');
                },

                setLastSeq: (seq: number) => {
                    set((state) => ({
                        console: {
                            ...state.console,
                            lastSeq: seq,
                        },
                    }), false, 'setLastSeq');
                },

                // UI state
                openConsolePanel: () => set({ isConsolePanelOpen: true }, false, 'openConsolePanel'),
                closeConsolePanel: () => set({ isConsolePanelOpen: false }, false, 'closeConsolePanel'),
                toggleConsolePanel: () => set((state) => ({ isConsolePanelOpen: !state.isConsolePanelOpen }), false, 'toggleConsolePanel'),
                
                openJobsSidebar: () => set({ isJobsSidebarOpen: true }, false, 'openJobsSidebar'),
                closeJobsSidebar: () => set({ isJobsSidebarOpen: false }, false, 'closeJobsSidebar'),
                toggleJobsSidebar: () => set((state) => ({ isJobsSidebarOpen: !state.isJobsSidebarOpen }), false, 'toggleJobsSidebar'),

                // Connection state
                setConnected: (connected: boolean) => set({ isConnected: connected }, false, 'setConnected'),
                setError: (error: string | null) => set({ lastError: error }, false, 'setError'),
                setLoading: (loading: boolean) => set({ isLoading: loading }, false, 'setLoading'),
                setPolling: (polling: boolean) => set({ isPolling: polling }, false, 'setPolling'),

                // Derived state getters
                getActiveJobs: () => {
                    const { jobs } = get();
                    return Array.from(jobs.values()).filter(job => 
                        job.status === 'RUNNING' || job.status === 'CANCEL_REQUESTED'
                    );
                },

                getQueuedJobs: () => {
                    const { jobs } = get();
                    return Array.from(jobs.values()).filter(job => 
                        job.status === 'QUEUED' || job.status === 'INITIALIZING'
                    );
                },

                getCompletedJobs: () => {
                    const { jobs } = get();
                    return Array.from(jobs.values())
                        .filter(job => job.status === 'COMPLETED')
                        .sort((a, b) => {
                            const aTime = a.timing.endedAt || a.timing.createdAt;
                            const bTime = b.timing.endedAt || b.timing.createdAt;
                            return new Date(bTime).getTime() - new Date(aTime).getTime();
                        })
                        .slice(0, 10); // Keep last 10
                },

                getFailedJobs: () => {
                    const { jobs } = get();
                    return Array.from(jobs.values())
                        .filter(job => ['FAILED', 'CANCELLED', 'ABORTED'].includes(job.status))
                        .sort((a, b) => {
                            const aTime = a.timing.endedAt || a.timing.createdAt;
                            const bTime = b.timing.endedAt || b.timing.createdAt;
                            return new Date(bTime).getTime() - new Date(aTime).getTime();
                        })
                        .slice(0, 5); // Keep last 5
                },

                getJobById: (jobId: string) => {
                    return get().jobs.get(jobId);
                },

                getAttachedJob: () => {
                    const { jobs, console } = get();
                    if (!console.jobId) return undefined;
                    return jobs.get(console.jobId);
                },

                getJobCounts: () => {
                    const { jobs } = get();
                    let active = 0;
                    let queued = 0;
                    let completed = 0;
                    let failed = 0;

                    jobs.forEach(job => {
                        if (job.status === 'RUNNING' || job.status === 'CANCEL_REQUESTED') {
                            active++;
                        } else if (job.status === 'QUEUED' || job.status === 'INITIALIZING') {
                            queued++;
                        } else if (job.status === 'COMPLETED') {
                            completed++;
                        } else if (['FAILED', 'CANCELLED', 'ABORTED'].includes(job.status)) {
                            failed++;
                        }
                    });

                    return { active, queued, completed, failed };
                },
            }),
            {
                name: 'jobs-store',
                // Only persist UI preferences, not job data (that comes from server)
                partialize: (state) => ({
                    isConsolePanelOpen: state.isConsolePanelOpen,
                    console: {
                        autoScroll: state.console.autoScroll,
                    },
                }),
            }
        ),
        { name: 'JobsStore' }
    )
);

/**
 * Selector hooks for common derived state
 */
export const useActiveJobs = () => useJobsStore((state) => state.getActiveJobs());
export const useQueuedJobs = () => useJobsStore((state) => state.getQueuedJobs());
export const useCompletedJobs = () => useJobsStore((state) => state.getCompletedJobs());
export const useJobCounts = () => useJobsStore((state) => state.getJobCounts());
export const useAttachedJob = () => useJobsStore((state) => state.getAttachedJob());
export const useConsoleState = () => useJobsStore((state) => state.console);
export const useIsConsolePanelOpen = () => useJobsStore((state) => state.isConsolePanelOpen);
